import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import { CarouselCaptionEditor } from './CarouselCaptionEditor.jsx';
import {
  CAROUSEL_SLIDES_UPDATED_EVENT,
  slideBlobDomProps,
} from '../lib/carousel_blob_refresh.js';

// Per-slide bubble menu: a small toolbar pinned over the currently-displayed
// carousel slide that mirrors the image/video bubble menu but is tailored
// for carousel context. Per the spec it intentionally drops the Caption
// button (the rich caption field below uses the main post toolbar when focused)
// and adds an "Edit carousel" pencil that opens ResourceModal in edit mode.
//
// Events dispatched:
//   tiptap:resource:requestTags { resourceId } — opens the resource tags
//     modal for THIS slide's underlying resource (each slide is itself a
//     resource). Only rendered when the slide has a resourceId; legacy
//     carousels without ids skip the Tags button gracefully and pick it up
//     once edited (Edit carousel → re-pick reseeds resourceIds).
//   tiptap:video:uploadThumbnail { resourceId } — same as standalone video;
//     MediaNodeFloating handles upload and updates carousel slides + video nodes.
//   tiptap:carouselSlide:requestReplace { nodePos, slideIdx } — replaces the
//     targeted slide's media via ResourceModal (single-pick).
//   tiptap:carousel:requestEdit { nodePos, slides } — opens ResourceModal in
//     edit-carousel mode (pre-selects current members, allows add/remove).
//
// Both event names are consumed by TiptapToolbar.jsx, which owns the modal.
function CarouselSlideBubbleMenu({
  nodePos,
  slideIdx,
  slides,
  slideResourceId,
  slideIsVideo,
  onDeleteCarousel,
}) {
  const handleEditCarousel = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (typeof nodePos !== 'number') return;
    const ev = new CustomEvent('tiptap:carousel:requestEdit', {
      bubbles: true,
      cancelable: true,
      detail: { nodePos, slides: Array.isArray(slides) ? slides : [] },
    });
    document.dispatchEvent(ev);
  }, [nodePos, slides]);

  const handleReplaceSlide = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (typeof nodePos !== 'number' || typeof slideIdx !== 'number') return;
    const ev = new CustomEvent('tiptap:carouselSlide:requestReplace', {
      bubbles: true,
      cancelable: true,
      detail: { nodePos, slideIdx },
    });
    document.dispatchEvent(ev);
  }, [nodePos, slideIdx]);

  const handleDelete = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (typeof onDeleteCarousel === 'function') onDeleteCarousel();
  }, [onDeleteCarousel]);

  const handleOpenTags = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!slideResourceId) return;
    const ev = new CustomEvent('tiptap:resource:requestTags', {
      bubbles: true,
      cancelable: true,
      detail: { resourceId: String(slideResourceId) },
    });
    document.dispatchEvent(ev);
  }, [slideResourceId]);

  const handleUploadThumbnail = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!slideResourceId || !slideIsVideo) return;
    document.dispatchEvent(
      new CustomEvent('tiptap:video:uploadThumbnail', {
        bubbles: true,
        detail: { resourceId: String(slideResourceId) },
      })
    );
  }, [slideResourceId, slideIsVideo]);

  return (
    <div
      className="post-content-carousel__slide-toolbar"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {slideResourceId && (
        <>
          <button
            type="button"
            title="Tags"
            aria-label="Tags"
            className="post-content-carousel__slide-tbtn"
            onClick={handleOpenTags}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83ZM6.5 7.5A1 1 0 1 1 7.5 6.5 1 1 0 0 1 6.5 7.5Z" />
            </svg>
          </button>
          <span className="post-content-carousel__slide-tsep" aria-hidden />
        </>
      )}
      {slideResourceId && slideIsVideo && (
        <>
          <button
            type="button"
            title="Upload thumbnail"
            aria-label="Upload thumbnail"
            className="post-content-carousel__slide-tbtn"
            onClick={handleUploadThumbnail}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2V4H16C15.4477 4 15 4.44772 15 5C15 5.55228 15.4477 6 16 6H18V8C18 8.55228 18.4477 9 19 9C19.5523 9 20 8.55228 20 8V6H22C22.5523 6 23 5.55228 23 5C23 4.44772 22.5523 4 22 4H20V2ZM5 4C4.73478 4 4.48043 4.10536 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H5.58579L14.379 11.2068C14.9416 10.6444 15.7045 10.3284 16.5 10.3284C17.2955 10.3284 18.0584 10.6444 18.621 11.2068L20 12.5858V12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12V14.998C22 14.9994 22 15.0007 22 15.002V19C22 19.7957 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7957 22 19 22H6.00219C6.00073 22 5.99927 22 5.99781 22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7957 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H12C12.5523 2 13 2.44772 13 3C13 3.55228 12.5523 4 12 4H5ZM8.41422 20H19C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8946 19.5196 20 19.2652 20 19V15.4142L17.207 12.6212C17.0195 12.4338 16.7651 12.3284 16.5 12.3284C16.2349 12.3284 15.9806 12.4337 15.7931 12.6211L8.41422 20ZM6.87868 6.87868C7.44129 6.31607 8.20435 6 9 6C9.79565 6 10.5587 6.31607 11.1213 6.87868C11.6839 7.44129 12 8.20435 12 9C12 9.79565 11.6839 10.5587 11.1213 11.1213C10.5587 11.6839 9.79565 12 9 12C8.20435 12 7.44129 11.6839 6.87868 11.1213C6.31607 10.5587 6 9.79565 6 9C6 8.20435 6.31607 7.44129 6.87868 6.87868ZM9 8C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9C8 9.26522 8.10536 9.51957 8.29289 9.70711C8.48043 9.89464 8.73478 10 9 10C9.26522 10 9.51957 9.89464 9.70711 9.70711C9.89464 9.51957 10 9.26522 10 9C10 8.73478 9.89464 8.48043 9.70711 8.29289C9.51957 8.10536 9.26522 8 9 8Z"
              />
            </svg>
          </button>
          <span className="post-content-carousel__slide-tsep" aria-hidden />
        </>
      )}
      <button
        type="button"
        title="Replace media"
        aria-label="Replace media"
        className="post-content-carousel__slide-tbtn"
        onClick={handleReplaceSlide}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
      </button>
      <span className="post-content-carousel__slide-tsep" aria-hidden />
      <button
        type="button"
        title="Edit carousel"
        aria-label="Edit carousel"
        className="post-content-carousel__slide-tbtn"
        onClick={handleEditCarousel}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
          <path d="m15 5 4 4" />
        </svg>
      </button>
      <span className="post-content-carousel__slide-tsep" aria-hidden />
      <button
        type="button"
        title="Delete carousel"
        aria-label="Delete carousel"
        className="post-content-carousel__slide-tbtn"
        onClick={handleDelete}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 6h18" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <line x1="10" x2="10" y1="11" y2="17" />
          <line x1="14" x2="14" y1="11" y2="17" />
        </svg>
      </button>
    </div>
  );
}

export function PostImageCarouselView({ node, updateAttributes, selected, getPos, deleteNode }) {
  const slides = Array.isArray(node.attrs.slides) ? node.attrs.slides : [];
  const indentLevel = node.attrs?.indent;
  const indentDomProps =
    typeof indentLevel === 'number' && indentLevel > 0
      ? { 'data-ps-indent': String(indentLevel) }
      : {};
  const clampIndex = useCallback((value, length = slides.length) => {
    const max = Math.max(0, length - 1);
    return Number.isFinite(value) ? Math.min(Math.max(value, 0), max) : 0;
  }, [slides.length]);
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);
  const swiperRef = useRef(null);
  const carouselRootRef = useRef(null);

  // When refresh_signed_blob_urls updates slide media, sync URLs into TipTap state
  // so React re-renders do not restore expired SAS links from stale attrs.
  useEffect(() => {
    const handler = (e) => {
      const root = carouselRootRef.current;
      if (!root || e.detail?.carouselRoot !== root) return;
      const nextSlides = e.detail?.slides;
      if (!Array.isArray(nextSlides)) return;
      try {
        if (JSON.stringify(slides) === JSON.stringify(nextSlides)) return;
      } catch {
        /* compare failed — still apply */
      }
      updateAttributes({ slides: nextSlides });
    };
    document.addEventListener(CAROUSEL_SLIDES_UPDATED_EVENT, handler);
    return () => document.removeEventListener(CAROUSEL_SLIDES_UPDATED_EVENT, handler);
  }, [slides, updateAttributes]);

  // Hide the per-slide bubble toolbar while ResourceModal is open. The modal
  // is rendered as a fixed overlay at the document body, so without this the
  // toolbar would visually leak through during edit/replace flows.
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    const checkModal = () => {
      const modalOverlay = document.querySelector('.resource-modal-overlay');
      setIsModalOpen((prev) => {
        const next = !!modalOverlay;
        return next === prev ? prev : next;
      });
    };
    const interval = setInterval(checkModal, 100);
    const observer = new MutationObserver(checkModal);
    observer.observe(document.body, { childList: true, subtree: true });
    checkModal();
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const nextIndex = clampIndex(index);
    if (nextIndex !== index) setIndex(nextIndex);
  }, [slides.length, index, clampIndex]);

  const safeIndex = clampIndex(index);
  const current = slides[safeIndex] || { type: 'image', src: '', poster: '', caption: '', alt: '' };

  const pauseInactiveVideos = useCallback(() => {
    const root = trackRef.current;
    if (!root) return;
    const active = root.querySelector('.post-content-carousel__slide.swiper-slide-active');
    root.querySelectorAll('.post-content-carousel__slide video').forEach((video) => {
      if (active && active.contains(video)) return;
      if (video && !video.paused) {
        try { video.pause(); } catch (e) {}
      }
    });
  }, []);

  const canLoop = useMemo(() => slides.length > 1, [slides.length]);
  // Swiper reinit + remounting <video> on every poster change breaks native controls
  // (Swiper's pointer handling can sit above the control bar). Poster is a normal
  // attribute update — do not key structure or the video element on it.
  const slidesStructureKey = useMemo(
    () =>
      slides
        .map((s) => `${s.type || 'image'}|${s.src || ''}|${String(s.resourceId ?? '')}`)
        .join('||'),
    [slides]
  );

  useEffect(() => {
    // Destroy existing Swiper when slides change so it fully reinitializes.
    // This ensures reordered slides are properly bound, not just updated.
    if (swiperRef.current) {
      try {
        swiperRef.current?.destroy?.(true, true);
      } catch (e) {}
      swiperRef.current = null;
    }

    const el = trackRef.current;
    if (!el || slides.length <= 1) return;

    swiperRef.current = new Swiper(el, {
      modules: [Navigation],
      slidesPerView: 1,
      loop: canLoop,
      initialSlide: clampIndex(index, slides.length),
      on: {
        init: (sw) => {
          setIndex(clampIndex(sw.realIndex));
          pauseInactiveVideos();
        },
        slideChange: (sw) => {
          setIndex(clampIndex(sw.realIndex));
          pauseInactiveVideos();
        },
      },
    });

    return () => {
      try {
        swiperRef.current?.destroy?.(true, true);
      } catch (e) {}
      swiperRef.current = null;
    };
  }, [slidesStructureKey, slides.length, canLoop, pauseInactiveVideos, clampIndex, index]);

  const setCaption = useCallback(
    (text) => {
      const next = slides.map((s, i) =>
        i === safeIndex ? { ...s, caption: text } : s
      );
      updateAttributes({ slides: next });
    },
    [slides, safeIndex, updateAttributes]
  );

  const prev = useCallback(() => {
    const sw = swiperRef.current;
    if (sw?.slidePrev) return sw.slidePrev();
    setIndex((i) => (slides.length ? clampIndex(i - 1 + slides.length) : 0));
  }, [slides.length, clampIndex]);

  const next = useCallback(() => {
    const sw = swiperRef.current;
    if (sw?.slideNext) return sw.slideNext();
    setIndex((i) => (slides.length ? clampIndex(i + 1) : 0));
  }, [slides.length, clampIndex]);

  // Resolve the carousel's node position so the bubble menu can dispatch
  // edit/replace events with a stable reference. `getPos` is only available
  // when running inside the editor; in rendered output it's undefined and
  // we simply skip rendering the toolbar.
  const nodePos = typeof getPos === 'function' ? getPos() : null;
  const showSlideToolbar =
    typeof nodePos === 'number' && nodePos >= 0 && !isModalOpen;

  if (!slides.length) {
    return (
      <NodeViewWrapper
        className="post-content-carousel post-content-carousel--empty"
        data-post-carousel="1"
        {...indentDomProps}
      >
        <p className="post-content-carousel__empty-hint">
          Use the carousel toolbar control to add images.
        </p>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      ref={carouselRootRef}
      className={`post-content-carousel${selected ? ' post-content-carousel--selected' : ''}`}
      data-post-carousel="1"
      data-ps-carousel-slides={JSON.stringify(slides)}
      {...indentDomProps}
    >
      <div className="post-content-carousel__track swiper" ref={trackRef}>
        <div className="post-content-carousel__track-inner swiper-wrapper">
          {slides.map((slide, i) => (
            <div
              key={`${slide.src}-${i}`}
              className="post-content-carousel__slide swiper-slide"
              data-carousel-index={String(i)}
              {...(slide.resourceId ? { 'data-resource-id': String(slide.resourceId) } : {})}
            >
              {slide.type === 'video' ? (
                <video
                  key={`${slide.src}|${slide.type}|${i}`}
                  className="post-content-carousel__video"
                  src={slide.src}
                  poster={slide.poster || undefined}
                  controls
                  playsInline
                  preload="metadata"
                  {...slideBlobDomProps(slide)}
                />
              ) : (
                <img
                  className="post-content-carousel__img"
                  src={slide.src}
                  alt={slide.alt || ''}
                  draggable={false}
                  {...slideBlobDomProps(slide)}
                />
              )}
              {/* Per-slide bubble menu mounts inside the active slide so it
                  visually pins to the media. The Tags button is gated on the
                  slide having a resourceId — new or freshly-edited carousels
                  carry one per slide; legacy ones pre-dating the schema bump
                  simply skip the Tags affordance. */}
              {showSlideToolbar && i === safeIndex && (
                <CarouselSlideBubbleMenu
                  nodePos={nodePos}
                  slideIdx={i}
                  slides={slides}
                  slideResourceId={slides[safeIndex]?.resourceId || null}
                  slideIsVideo={slides[safeIndex]?.type === 'video'}
                  onDeleteCarousel={() => {
                    if (typeof deleteNode === 'function') deleteNode();
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="post-content-carousel__caption-wrap">
        <CarouselCaptionEditor
          key={`carousel-caption-${safeIndex}`}
          captionHtml={current.caption || ''}
          onCaptionChange={setCaption}
          editable
        />
      </div>
      <div className="post-content-carousel__rule" aria-hidden />
      <div className="post-content-carousel__nav">
        <button
          type="button"
          className="post-content-carousel__nav-btn"
          data-carousel-prev="1"
          onClick={prev}
          aria-label="Previous slide"
        >
          <span className="icon-ps-arrow-left" aria-hidden="true" />
        </button>
        <span className="post-content-carousel__counter">
          <span className="post-content-carousel__counter-num">{safeIndex + 1}</span>
          <span className="post-content-carousel__counter-of">of</span>
          <span className="post-content-carousel__counter-num">{slides.length}</span>
        </span>
        <button
          type="button"
          className="post-content-carousel__nav-btn"
          data-carousel-next="1"
          onClick={next}
          aria-label="Next slide"
        >
          <span className="icon-ps-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </NodeViewWrapper>
  );
}
